import { isNumber } from 'lodash';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Cell } from 'src/components/organisms/DataTable/types';
import { Module } from 'src/domain';
import { SalesCampaignOverviewRow } from 'src/domain/table/sales-campaign-overview.row';
import { ExtendedSalesCampaignDiscountType } from 'src/infrastructure/rest-api/api-types';
import { findRow } from 'src/utils/ag-grid/findRow';
import { parseNumber } from 'src/utils/formatNumber';
import { applySalesCampaignDiscountCellValue, changeSalesCampaignDiscountType } from 'src/utils/salesCampaignDiscount';
import { useUnsavedChangesModule } from '../unsaved-changes';
import { useSalesCampaignSimulationRows } from './useSalesCampaignSimulationRows';

function getDiscountTypeChange(cell: Cell, currentData: SalesCampaignOverviewRow) {
    return {
        discount: changeSalesCampaignDiscountType(
            currentData,
            (cell.value ?? 'FIXED_PRICE') as ExtendedSalesCampaignDiscountType
        ),
    };
}

function getDiscountChange(cell: Cell, currentData: SalesCampaignOverviewRow) {
    return {
        discount: applySalesCampaignDiscountCellValue(currentData, cell.value),
    };
}

function getDiscountPercentageChange(cell: Cell, currentData: SalesCampaignOverviewRow) {
    return {
        discount: {
            ...currentData.discount,
            discountType: 'PERCENTAGE' as ExtendedSalesCampaignDiscountType,
            valueX: 1,
            valueY: isNumber(cell.value) ? parseNumber(cell.value) : null,
            valueZ: null,
        },
    };
}

export function getNewData(cell: Cell, currentData: SalesCampaignOverviewRow) {
    switch (cell.column) {
        case 'discount.comment':
            return {
                discount: {
                    ...currentData.discount,
                    comment: cell.value,
                },
            };

        case 'discount.discountType':
            return getDiscountTypeChange(cell, currentData);

        case 'discount':
            return getDiscountChange(cell, currentData);

        case 'discountPercentage':
            return getDiscountPercentageChange(cell, currentData);
        default:
    }

    if (cell.column) {
        return {
            [cell.column]: cell.value,
        };
    }

    return {};
}

export const useSalesCampaignPlanningCellValuesChanged = () => {
    const { id: salesCampaignId } = useParams();
    const { data: simulationData } = useSalesCampaignSimulationRows(Number(salesCampaignId));
    const [unsavedChanges, setUnsavedChanges] = useUnsavedChangesModule<SalesCampaignOverviewRow[]>(
        Module.SalesCampaignsPlanning
    );

    return useCallback(
        (cells: Cell[]) => {
            const newUnsavedChanges = [...(unsavedChanges ?? [])];

            cells.forEach((cell) => {
                if (cell.column) {
                    const { rowIndexToUpdate, simulationIndexToUpdate, currentData } = findRow(
                        cell,
                        newUnsavedChanges,
                        simulationData
                    );

                    const newData = getNewData(cell, currentData);

                    if (rowIndexToUpdate >= 0) {
                        newUnsavedChanges[rowIndexToUpdate] = {
                            ...newUnsavedChanges?.[rowIndexToUpdate],
                            ...newData,
                        };
                    } else {
                        newUnsavedChanges.push({
                            ...simulationData[simulationIndexToUpdate],
                            ...newData,
                        } as SalesCampaignOverviewRow);
                    }
                }
            });

            setUnsavedChanges(newUnsavedChanges);
        },
        [setUnsavedChanges, simulationData, unsavedChanges]
    );
};
